import React, { createRef, FC, useState } from 'react';
import './styles.scss';
import Draggable, { DraggableEvent, DraggableData } from 'react-draggable';
import { ModalProps, Modal as AntdModal } from 'antd';

/**
 * @param draggable Set modal draggable
 * @param headerTitle Set the title element of the header
 * @param onClose Function to be called onClose
 * extends {@link ModalProps}
 */
export interface IModalProps extends ModalProps {
	draggable?: boolean;
	headerTitle?: React.ReactNode | string;
	onClose?: () => void;
}

/**
 * ## Modal
 * @example <Modal {...props} footer={[some_buttons]} closable>
			<div className={'modal-content'}>
				<h4>{'some_content'}</h4>
			</div>
		</Modal>				
 * @param props See {@link IModalProps}
 * @returns a modal component
 */
export const Modal: FC<IModalProps> = (props) => {
	const [getDisabledState, setDisabledState] = useState<boolean>(true);
	const [getBoundsState, setBoundsState] = useState({
		left: 0,
		top: 0,
		bottom: 0,
		right: 0,
	});

	const draggleRef = createRef<HTMLDivElement>();

	/**
	 * Draggable modal controller
	 * Updates the modal bounds on drag
	 */
	const onStart = (event: DraggableEvent, uiData: DraggableData) => {
		const { clientWidth, clientHeight } = window.document.documentElement;
		const targetRect = draggleRef.current?.getBoundingClientRect();
		if (targetRect) {
			setBoundsState({
				left: -targetRect.left + uiData.x,
				right: clientWidth - (targetRect.right - uiData.x),
				top: -targetRect.top + uiData.y,
				bottom: clientHeight - (targetRect.bottom - uiData.y),
			});
		}
	};

	/**
	 * Draggable header wrapper element for title.
	 * If props.draggable is set we render this, to allow dragging.
	 * @returns a header with a draggable cursor indicator
	 */
	const DraggableHeader: React.FC<{ title: React.ReactNode | string }> = (props) => {
		return (
			<div className={'draggable-header'} onMouseOver={() => getDisabledState && setDisabledState(false)} onMouseOut={() => setDisabledState(true)}>
				{props.title}
			</div>
		);
	};

	return (
		<AntdModal
			{...props}
			title={props.draggable ? <DraggableHeader title={props.title} /> : props.title}
			centered
			destroyOnClose
			onCancel={props.onClose}
			modalRender={(modal) => (
				<Draggable nodeRef={draggleRef} disabled={getDisabledState} bounds={getBoundsState} onStart={(event, uiData) => onStart(event, uiData)}>
					<div ref={draggleRef}>{modal}</div>
				</Draggable>
			)}>
			<div>{props.children}</div>
		</AntdModal>
	);
};
